"use client";

import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";
import { EmptyState, type EmptyStateProps } from "./empty-state";
import { FOCUS_RING } from "./focus-ring";
import { HOVER_EASE } from "./states";

// Design spec §6.4: the table sits in a hairline card (radius 11); headers are 12 px uppercase
// captions on --sunken, cells 13 px with 12 / 16 padding, rows divided by --border-subtle.
function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-auto rounded-lg border border-border bg-card"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom border-collapse text-sm", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b [&_tr]:border-border [&_tr]:hover:bg-transparent", className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn("border-t border-border bg-sunken font-ui [&>tr]:last:border-b-0", className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b border-subtle transition-colors duration-(--dur-fast) hover:bg-sunken has-aria-expanded:bg-sunken aria-selected:bg-primary-soft data-[state=selected]:bg-primary-soft",
        className,
      )}
      {...props}
    />
  );
}

type SortDirection = "ascending" | "descending" | "none";

const SORT_ICON = {
  ascending: ArrowUpIcon,
  descending: ArrowDownIcon,
  none: ArrowUpDownIcon,
} as const;

function TableHead({
  className,
  numeric = false,
  sort,
  onSort,
  children,
  ...props
}: React.ComponentProps<"th"> & {
  /** Right-aligned tabular column. */
  numeric?: boolean;
  /** Sort state of a sortable column (sets `aria-sort`). */
  sort?: SortDirection;
  /** Makes the caption a button; the icon shows on hover/focus, or always once sorted. */
  onSort?: () => void;
}) {
  const sortable = onSort !== undefined;
  const direction = sort ?? "none";
  const Icon = SORT_ICON[direction];
  return (
    <th
      data-slot="table-head"
      aria-sort={sortable ? direction : undefined}
      className={cn(
        "h-10 bg-sunken px-4 text-left align-middle text-xs font-semibold tracking-[0.7px] whitespace-nowrap text-muted-foreground uppercase [&:has([role=checkbox])]:pr-0",
        // Column captions stay visible over a scrolling body.
        "[thead_&]:sticky [thead_&]:top-0 [thead_&]:z-sticky",
        // A row header (`<th scope="row">`) reads as the row's name, not as a column caption.
        "[&[scope=row]]:h-auto [&[scope=row]]:bg-transparent [&[scope=row]]:py-3 [&[scope=row]]:text-sm [&[scope=row]]:font-medium [&[scope=row]]:tracking-normal [&[scope=row]]:text-foreground [&[scope=row]]:normal-case",
        numeric && "text-right tabular-nums",
        className,
      )}
      {...props}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            "group/sort -mx-1 inline-flex items-center gap-1 rounded-xs px-1 uppercase hover:text-foreground",
            numeric && "flex-row-reverse",
            HOVER_EASE,
            FOCUS_RING,
          )}
        >
          {children}
          <Icon
            aria-hidden={true}
            className={cn(
              "size-3.5 shrink-0",
              direction === "none" &&
                "opacity-0 group-hover/sort:opacity-100 group-focus-visible/sort:opacity-100 pointer-coarse:opacity-100",
            )}
          />
        </button>
      ) : (
        children
      )}
    </th>
  );
}

function TableCell({
  className,
  numeric = false,
  ...props
}: React.ComponentProps<"td"> & { numeric?: boolean }) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "px-4 py-3 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0",
        numeric && "text-right tabular-nums",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({
  className,
  side = "bottom",
  ...props
}: React.ComponentProps<"caption"> & { side?: "top" | "bottom" }) {
  return (
    <caption
      data-slot="table-caption"
      className={cn(
        "px-4 py-3 text-xs text-muted-foreground",
        side === "top" ? "caption-top text-left" : "caption-bottom",
        className,
      )}
      {...props}
    />
  );
}

/** An empty body: one row whose cell spans the table and holds a compact `EmptyState`. */
function TableEmpty({ colSpan, ...props }: Omit<EmptyStateProps, "variant"> & { colSpan: number }) {
  return (
    <tr data-slot="table-empty" className="hover:bg-transparent">
      <td colSpan={colSpan} className="p-0">
        <EmptyState variant="compact" {...props} />
      </td>
    </tr>
  );
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableEmpty,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
